import { clearAccessToken, setAccessToken, setRole, setUsername, type SessionRole } from '../core/index.js'
import type { DemoSessionResponse } from './index.js'

/**
 * How a demo session reaches a new tab. The opener (the admin area, or a
 * demo operator's shell) opens a tab at the shell's path with this marker,
 * the tab says it is ready, and the opener posts the session over a
 * same-origin message. The token never goes in a URL, never in
 * localStorage: it lands in that tab's sessionStorage, as every real
 * sign-in does, and dies with the tab.
 */
export const DEMO_SESSION_MARKER = 'demo-session'
const INFO_KEY = 'autonome-web:demoSession'
const READY = 'autonome-demo-session:ready'
/** The opener asking a tab that may have announced itself before anyone was listening to say so again. */
const PING = 'autonome-demo-session:ping'
const SESSION = 'autonome-demo-session'
const FAILED = 'autonome-demo-session:failed'
const DEMO_ENDED_CHANNEL = 'autonome-demo-session'
const DEMO_ENDED = 'autonome-demo-session:ended'
/** How long the opener waits for the new tab to say it is ready before giving up. */
const HANDOFF_TIMEOUT_MS = 15_000

/** What the shell shows in its banner; kept beside the token for the tab's life. */
export interface DemoSessionInfo {
  role: SessionRole
  /** The operatorId, administratorId or workerId this tab is. */
  subject: string
  username: string
  name: string | null
  openedBy: string
  expiresAt: string
  /** True when a SeeWhoCares admin opened the session: the tab then names the API call behind each section, as the admin area does (components/EndpointHint.tsx). */
  showsEndpoints?: boolean
}

interface SessionMessage {
  type: typeof SESSION
  accessToken: string
  info: DemoSessionInfo
}

export function getDemoSessionInfo(): DemoSessionInfo | null {
  try {
    const raw = sessionStorage.getItem(INFO_KEY)
    return raw ? (JSON.parse(raw) as DemoSessionInfo) : null
  } catch {
    return null
  }
}

/** Whether this tab is a demo session a SeeWhoCares admin opened, and so shows the endpoint paths an admin sees. */
export function demoSessionShowsEndpoints(): boolean {
  return getDemoSessionInfo()?.showsEndpoints === true
}

export function clearDemoSessionInfo(): void {
  try {
    sessionStorage.removeItem(INFO_KEY)
  } catch {
    // ignore
  }
}

/** True when this tab was opened to receive a demo session and has not yet. */
export function isAwaitingDemoSessionHandoff(): boolean {
  return new URLSearchParams(window.location.search).has(DEMO_SESSION_MARKER)
}

/** The path the tab will show once signed in: the current one without the marker. */
function pathWithoutMarker(): string {
  const params = new URLSearchParams(window.location.search)
  params.delete(DEMO_SESSION_MARKER)
  const query = params.toString()
  return `${window.location.pathname}${query ? `?${query}` : ''}`
}

function isSessionMessage(data: unknown): data is SessionMessage {
  if (!data || typeof data !== 'object') {
    return false
  }
  const message = data as Partial<SessionMessage>
  return message.type === SESSION && typeof message.accessToken === 'string' && typeof message.info === 'object' && message.info !== null
}

/**
 * The receiving side, run once by the tab that was opened with the marker.
 * Accepts a session from its opener only, and only from its own origin,
 * stores it for this tab, and reloads at the clean path so the shell
 * starts signed in. Calls `onFailed` if the opener reports it could not
 * open the session.
 */
export function receiveDemoSessionHandoff(onFailed: (message: string) => void): () => void {
  // A tab opened from another starts with a copy of the opener's sessionStorage. This tab is for the demo session alone, so that copy goes first: if the hand-off fails, nothing is left signed in here.
  clearAccessToken()
  clearDemoSessionInfo()
  const openerWindow = window.opener as Window | null
  if (!openerWindow) {
    onFailed('This tab was not opened from the admin area or a demo session, so there is no session to receive.')
    return () => {}
  }
  const opener: Window = openerWindow
  function onMessage(event: MessageEvent<unknown>): void {
    if (event.origin !== window.location.origin || event.source !== opener) {
      return
    }
    const data = event.data
    if (isSessionMessage(data)) {
      setAccessToken(data.accessToken)
      setUsername(data.info.username)
      setRole(data.info.role)
      try {
        sessionStorage.setItem(INFO_KEY, JSON.stringify(data.info))
      } catch {
        // The banner will have nothing to say; the session still works for this tab.
      }
      window.removeEventListener('message', onMessage)
      // The hand-off is done; the signed-in tab keeps no handle on the tab that opened it.
      window.opener = null
      window.location.replace(pathWithoutMarker())
      return
    }
    if (data && typeof data === 'object' && (data as { type?: unknown }).type === PING) {
      opener.postMessage({ type: READY }, window.location.origin)
      return
    }
    if (data && typeof data === 'object' && (data as { type?: unknown }).type === FAILED) {
      onFailed(typeof (data as { message?: unknown }).message === 'string' ? ((data as { message: string }).message) : 'The session could not be opened.')
    }
  }
  window.addEventListener('message', onMessage)
  opener.postMessage({ type: READY }, window.location.origin)
  return () => window.removeEventListener('message', onMessage)
}

/**
 * A demo session switching to someone else in the same tab: the new token
 * replaces the old one in this tab's storage and the tab reloads into the
 * shell for the new role, with nothing carried over from the last person.
 */
export function adoptDemoSession(response: DemoSessionResponse): void {
  // A switch stays the same opener's session, so it shows the endpoint paths exactly when the session it replaces did.
  const info: DemoSessionInfo = { role: response.role, subject: response.session.subject, username: response.username, name: response.name, openedBy: response.session.openedBy, expiresAt: response.session.expiresAt, showsEndpoints: demoSessionShowsEndpoints() }
  setAccessToken(response.accessToken)
  setUsername(response.username)
  setRole(response.role)
  try {
    sessionStorage.setItem(INFO_KEY, JSON.stringify(info))
  } catch {
    // The banner will have less to say; the session still works for this tab.
  }
  window.location.replace(response.role === 'worker' ? '/worker' : '/')
}

/**
 * A demo session given more time: the new token replaces the old one in this
 * tab and the stored end moves, with no reload, so the screen stays as it is.
 * Answers what the banner now shows.
 */
export function adoptExtendedDemoSession(response: DemoSessionResponse): DemoSessionInfo | null {
  setAccessToken(response.accessToken)
  const current = getDemoSessionInfo()
  if (!current) {
    return null
  }
  const info: DemoSessionInfo = { ...current, expiresAt: response.session.expiresAt }
  try {
    sessionStorage.setItem(INFO_KEY, JSON.stringify(info))
  } catch {
    // The banner keeps the old end on a reload; the new token still works.
  }
  return info
}

/**
 * Leaving a demo session for the account that opened it. That account's
 * sign-in was never in this tab - an admin's credential lives only in the
 * memory of the tab they opened this from - so the way back is to end the
 * demo session here and close this tab, which returns to that one. If the
 * browser will not close it (the opening tab is gone, say), the tab goes to
 * that account's own sign-in instead.
 */
export function returnToOpener(openerIsAdmin: boolean): void {
  clearAccessToken()
  clearDemoSessionInfo()
  // Tell the tab this was opened from, so it is on its default page when this one closes onto it. Same-origin tabs only, and it carries no token.
  try {
    const channel = new BroadcastChannel(DEMO_ENDED_CHANNEL)
    channel.postMessage({ type: DEMO_ENDED, openerIsAdmin })
    channel.close()
  } catch {
    // No BroadcastChannel: the opening tab simply stays where it was.
  }
  window.close()
  // Still here a moment later means the browser would not close the tab: go to that account's own start page instead.
  window.setTimeout(() => window.location.replace(openerIsAdmin ? '/admin' : '/'), 150)
}

/**
 * The opening side of `returnToOpener`: calls `onEnded` when a demo session
 * this origin opened says it has ended and is closing. `forAdmin` picks the
 * sessions an admin opened, as against an operator's.
 */
export function onDemoSessionEnded(forAdmin: boolean, onEnded: () => void): () => void {
  let channel: BroadcastChannel
  try {
    channel = new BroadcastChannel(DEMO_ENDED_CHANNEL)
  } catch {
    return () => {}
  }
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const data = event.data as { type?: unknown; openerIsAdmin?: unknown } | null
    if (data?.type === DEMO_ENDED && data.openerIsAdmin === forAdmin) {
      onEnded()
    }
  }
  return () => channel.close()
}

/** The shell path for a role: the worker shell for a worker, the operator shell for everyone else. */
export function demoSessionTabUrl(role: SessionRole): string {
  return `${role === 'worker' ? '/worker' : '/'}?${DEMO_SESSION_MARKER}`
}

/**
 * The opening side. Call `openDemoSessionTab` synchronously inside the
 * click (so the browser treats the new tab as asked for), then
 * `handDemoSessionToTab` once the API has answered. The hand-off waits for
 * the tab's ready message, posts the session, and resolves; it rejects if
 * the tab never answers. `tellTabItFailed` lets the tab explain itself when
 * the API refused.
 */
export function openDemoSessionTab(role: SessionRole): Window | null {
  // No features string: one would make some browsers open a pop-up window, and the default keeps `opener`, which the hand-off needs.
  return window.open(demoSessionTabUrl(role), '_blank')
}

export function handDemoSessionToTab(tab: Window, response: DemoSessionResponse, showsEndpoints = false): Promise<void> {
  const info: DemoSessionInfo = { role: response.role, subject: response.session.subject, username: response.username, name: response.name, openedBy: response.session.openedBy, expiresAt: response.session.expiresAt, showsEndpoints }
  const message: SessionMessage = { type: SESSION, accessToken: response.accessToken, info }
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true
        window.removeEventListener('message', onMessage)
        reject(new Error('The new tab did not answer; it may have been blocked. Allow pop-ups for this site and try again.'))
      }
    }, HANDOFF_TIMEOUT_MS)
    function onMessage(event: MessageEvent<unknown>): void {
      if (event.origin !== window.location.origin || event.source !== tab) {
        return
      }
      if ((event.data as { type?: unknown } | null)?.type === READY && !settled) {
        settled = true
        window.clearTimeout(timer)
        window.removeEventListener('message', onMessage)
        tab.postMessage(message, window.location.origin)
        resolve()
      }
    }
    window.addEventListener('message', onMessage)
    // The tab usually loads and says ready while the API call is still out, before this listener exists - so ask it to say so again.
    tab.postMessage({ type: PING }, window.location.origin)
  })
}

export function tellTabItFailed(tab: Window, message: string): void {
  try {
    tab.postMessage({ type: FAILED, message }, window.location.origin)
  } catch {
    // The tab is gone; nothing to tell.
  }
}
